import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useNavigate } from "react-router";
import { Breadcrumbs as FoodsBreadcrumbs, FoodsIcon, FoodsTabs, FoodsTabTemplateOption } from "@foodpilot/foods";
import { useIngredientContext } from "./useIngredientContext";
import { VIEW_SSQ, INGREDIENT, useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { Loading } from "src/components/utils/Loading";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { ActiveOrganizationContextHeader } from "src/components/ActiveOrganizationContextHeader";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";
import { Breadcrumbs as ResponsiveBreadcrumbs, type BreadcrumbType } from "src/components/Breadcrumb/Breadcrumbs";

type SectionKeys = "scores" | "questionnaire" | "activities" | "informations" | "value-chain";
type IngredientDetailsProps = {
    selectedSection: SectionKeys;
};
export const IngredientPrimaryTabs = (props: IngredientDetailsProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isProductUxV2 = useIsProductUxV2();

    const { selectedSection } = props;
    const { selectedCompany, canUser } = useFoodpilotContext();
    const { applicationParameters } = useApplicationParameters();
    const { ingredient, isReady, questionnaireSupplierId, showIngredientSuppliers } = useIngredientContext();

    const isValueChainEnabled = applicationParameters?.["FS-product-value-chain"] === "true";

    if (isReady === false) return <Loading />;

    if (ingredient === undefined || selectedCompany === null) {
        return;
    }

    const tabs: FoodsTabTemplateOption<SectionKeys>[] = [];

    const scoreUrl = URLHandler.ingredient.scores(selectedCompany.id, ingredient.id);
    tabs.push({
        id: "scores",
        name: (
            <Link
                to={scoreUrl}
                style={{
                    textDecoration: "none",
                    color: "inherit",
                }}
            >
                <Stack flexDirection="row" alignItems={"center"} gap={theme.spacing(1)}>
                    <Box display={"flex"} paddingBottom={"2px"}>
                        <FoodsIcon size={2} icon="scoresIndicators" />
                    </Box>
                    <Typography variant="body-bold">{t("scores_indicateurs_cles")}</Typography>
                </Stack>
            </Link>
        ),
        TabProps: {
            onClick: () => {
                navigate(scoreUrl);
            },
        },
    });

    if (canUser(VIEW_SSQ, INGREDIENT) && questionnaireSupplierId !== null) {
        const questionnaireUrl = URLHandler.ingredient.ssq(selectedCompany.id, ingredient.id, questionnaireSupplierId);
        tabs.push({
            id: "questionnaire",
            name: (
                <Link
                    to={questionnaireUrl}
                    style={{
                        textDecoration: "none",
                        color: "inherit",
                    }}
                >
                    <Stack flexDirection="row" alignItems={"center"} gap={theme.spacing(1)}>
                        <Box display={"flex"} paddingBottom={"2px"}>
                            <FoodsIcon size={2} icon="questionnaires" />
                        </Box>
                        <Typography variant="body-bold">
                            {isProductUxV2 ? t("questionnaire") : t("questionnaires")}
                        </Typography>
                    </Stack>
                </Link>
            ),
            TabProps: {
                onClick: () => {
                    navigate(questionnaireUrl);
                },
            },
        });
    }

    if (!isProductUxV2 && showIngredientSuppliers) {
        const activitiesUrl = URLHandler.ingredient.activity(selectedCompany.id, ingredient.id);
        tabs.push({
            id: "activities",
            name: (
                <Link
                    to={activitiesUrl}
                    style={{
                        textDecoration: "none",
                        color: "inherit",
                    }}
                >
                    <Stack flexDirection="row" alignItems={"center"} gap={theme.spacing(1)}>
                        <Box display={"flex"} paddingBottom={"2px"}>
                            <FoodsIcon size={2} icon="activityData" />
                        </Box>
                        <Typography variant="body-bold">{t("donnees_d_activite")}</Typography>
                    </Stack>
                </Link>
            ),
            TabProps: {
                onClick: () => {
                    navigate(activitiesUrl);
                },
            },
        });
    }

    const informationUrl = URLHandler.ingredient.information(selectedCompany.id, ingredient.id);
    tabs.push({
        id: "informations",
        name: (
            <Link
                to={informationUrl}
                style={{
                    textDecoration: "none",
                    color: "inherit",
                }}
            >
                <Stack flexDirection="row" alignItems={"center"} gap={theme.spacing(1)}>
                    <Box display={"flex"} paddingBottom={"2px"}>
                        <FoodsIcon size={2} icon="informations" />
                    </Box>
                    <Typography variant="body-bold">{t("informations")}</Typography>
                </Stack>
            </Link>
        ),
        TabProps: {
            onClick: () => {
                navigate(informationUrl);
            },
        },
    });

    if (isValueChainEnabled) {
        const valueChainUrl = URLHandler.ingredient.valueChain(selectedCompany.id, ingredient.id);
        tabs.push({
            id: "value-chain",
            name: (
                <Link
                    to={valueChainUrl}
                    style={{
                        textDecoration: "none",
                        color: "inherit",
                    }}
                >
                    <Stack flexDirection="row" alignItems={"center"} gap={theme.spacing(1)}>
                        <Box display={"flex"} paddingBottom={"2px"}>
                            <FoodsIcon size={2} icon="graphLink" />
                        </Box>
                        <Typography variant="body-bold">{t("value_chain", "Value chain")}</Typography>
                    </Stack>
                </Link>
            ),
            TabProps: {
                onClick: () => {
                    navigate(valueChainUrl);
                },
            },
        });
    }

    const selectedTab = tabs.findIndex((i) => i.id === selectedSection);

    if (isProductUxV2) {
        const ingredientListUrl = URLHandler.ingredient.list(selectedCompany.id);
        const breadcrumbItems: BreadcrumbType[] = [
            {
                label: t("ingredient.v2.breadcrumb.list", "All raw materials"),
                action: () => navigate(ingredientListUrl),
            },
            { label: ingredient.name },
        ];

        return (
            <Box
                component="div"
                data-testid="ingredient-v2-details-shell"
                sx={{
                    marginInlineEnd: "auto",
                    marginInlineStart:
                        isMobile ? "auto" : `calc(var(--app-navigation-overlap, ${theme.spacing(2.5)}) * -1)`,
                }}
            >
                <Box
                    data-testid="ingredient-v2-details-header"
                    sx={{ backgroundColor: theme.custom.grey[100], position: "relative" }}
                >
                    <Box sx={{ paddingInline: theme.spacing(6), paddingTop: theme.spacing(3) }}>
                        {isMobile ?
                            <ResponsiveBreadcrumbs items={breadcrumbItems} />
                        :   <FoodsBreadcrumbs
                                ariaLabel={t("common.navigation.breadcrumb", "Breadcrumb")}
                                ancestors={[
                                    {
                                        id: "ingredient-details-list",
                                        label: breadcrumbItems[0].label,
                                        onSelect: breadcrumbItems[0].action!,
                                    },
                                ]}
                                current={{ id: "ingredient-details-current", label: ingredient.name }}
                            />
                        }
                    </Box>
                    <Box sx={{ marginTop: theme.spacing(1), paddingInline: theme.spacing(6) }}>
                        <FoodsTabs
                            forceScrollable
                            tabs={tabs}
                            selectedTabIndex={selectedTab >= 0 ? selectedTab : 0}
                            tabVariant="primary"
                        />
                    </Box>
                    <Box
                        aria-hidden
                        sx={{
                            borderBottomColor: theme.custom.strokes.containerDivider.color,
                            borderBottomStyle: theme.custom.strokes.containerDivider.style,
                            borderBottomWidth: theme.custom.strokes.containerDivider.width,
                            bottom: 0,
                            left: 0,
                            pointerEvents: "none",
                            position: "absolute",
                            right: 0,
                        }}
                    />
                </Box>
                <Stack
                    data-testid="ingredient-v2-details-content"
                    padding={theme.spacing(3, 6)}
                    sx={{ backgroundColor: theme.custom.grey[300], minHeight: "100%" }}
                >
                    <Outlet />
                </Stack>
            </Box>
        );
    }

    return (
        <Box component="div" sx={{ marginLeft: "auto", marginRight: "auto" }}>
            <Box
                sx={{
                    width: "auto",
                    background: "#fff",
                    padding: "28px 52px",
                    paddingBottom: 0,
                    position: "relative",
                    borderBottom: `1px solid ${theme.custom.grey[500]}`,
                }}
            >
                <Stack direction="row" alignItems="center" gap="16px">
                    <Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
                        <ArrowBackIcon
                            style={{
                                cursor: "pointer",
                                margin: "8px",
                            }}
                            onClick={() => navigate(URLHandler.ingredient.list(selectedCompany.id))}
                        />
                        <ActiveOrganizationContextHeader
                            title={<Typography variant="h1">{ingredient.name}</Typography>}
                        />
                    </Box>
                </Stack>

                <FoodsTabs forceScrollable tabs={tabs} selectedTabIndex={selectedTab ?? 0} tabVariant={"primary"} />
            </Box>

            <Stack
                gap="28px"
                sx={{
                    padding: theme.spacing(3, 6),
                }}
            >
                <Outlet />
            </Stack>
        </Box>
    );
};
